import { useEffect, useMemo, useState } from "react";
import type { ComponentProps } from "react";
import clsx from "clsx";
import { OrdnerPickerModal } from "@/components/filter/OrdnerPickerModal";
import { Spinner } from "@/components/ui/Spinner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useStatusSichtbarkeit } from "@/hooks/useStatusSichtbarkeit";
import {
  listKundenAllFromOrdner,
  listKundenForYear,
  listOrdner,
  listOrdnerForYearCustomer,
  listSOrdner,
  listYears,
  PROJECT_WORKFLOW_STATUS_LABELS,
  PROJECT_WORKFLOW_STATUSES,
} from "@/lib/projects";
import type { OrdnerOut, SOrdnerOut } from "@/lib/projects";
import type {
  ProjectFilters,
  StatusFilter,
  StatusFilterValue,
} from "@/pages/projekte/useProjectFilters";
import { projectHasActiveFilters } from "@/pages/projekte/useProjectFilters";
import {
  buildSOrdnerHeadingLine,
  formatSOrdnerLocation,
} from "@/pages/projekte/projectFilterSummary";

const ALL_STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "alle", label: "Alle" },
  ...PROJECT_WORKFLOW_STATUSES.map((value) => ({
    value,
    label: PROJECT_WORKFLOW_STATUS_LABELS[value],
  })),
];

function toggleStatusFilter(
  current: StatusFilterValue[],
  value: StatusFilter,
): StatusFilterValue[] {
  if (value === "alle") return [];
  return current.includes(value)
    ? current.filter((s) => s !== value)
    : [...current, value];
}

export interface FilterBarProps {
  filters: ProjectFilters;
  onChange: (patch: Partial<ProjectFilters>) => void;
  onReset?: () => void;
  /** Doppelklick auf den aktiven S-Ordner-Chip öffnet die S-Ordner-Übersicht. */
  onSOrdnerOpen?: (sordner: SOrdnerOut) => void;
}

/**
 * Zwei gleich breite Kacheln (lg): links Jahr/Kunde/Status — S‑Ordner unter der Jahres-Zeile;
 * rechts Ordner (Liste &amp; Suche); S‑Ordner in Zehner-Zeilen (S01–S09, S10–S19, …).
 */
export function FilterBar({ filters, onChange, onReset, onSOrdnerOpen }: FilterBarProps) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { visibleStatuses } = useStatusSichtbarkeit();
  const statusFilterOptions = useMemo(() => {
    const visible = new Set(visibleStatuses("projekte"));
    return ALL_STATUS_FILTER_OPTIONS.filter(
      (opt) => opt.value === "alle" || visible.has(opt.value),
    );
  }, [visibleStatuses]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [years, setYears] = useState<number[]>([]);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [kunden, setKunden] = useState<string[]>([]);
  const [kundenLoading, setKundenLoading] = useState(false);
  const [ordner, setOrdner] = useState<OrdnerOut[]>([]);
  const [ordnerLoading, setOrdnerLoading] = useState(false);
  const [sordner, setSOrdner] = useState<SOrdnerOut[]>([]);
  const [sordnerLoading, setSOrdnerLoading] = useState(false);
  const [ordnerModalOpen, setOrdnerModalOpen] = useState(false);
  const [sordnerQuickFilter, setSordnerQuickFilter] = useState("");

  const hasActiveFilters = projectHasActiveFilters(filters);
  const showFilterBody = !isMobile || filtersExpanded;

  useEffect(() => {
    let cancelled = false;
    setYearsLoading(true);
    listYears()
      .then((list) => {
        if (cancelled) return;
        const set = new Set(list);
        set.add(new Date().getFullYear());
        setYears([...set].sort((a, b) => b - a));
      })
      .catch(() => {
        if (!cancelled) setYears([new Date().getFullYear()]);
      })
      .finally(() => {
        if (!cancelled) setYearsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr === "S") {
      setKunden([]);
      return;
    }
    if (typeof filters.jahr === "number") {
      setKundenLoading(true);
      listKundenForYear(filters.jahr)
        .then((list) => {
          if (!cancelled) setKunden(list);
        })
        .catch(() => {
          if (!cancelled) setKunden([]);
        })
        .finally(() => {
          if (!cancelled) setKundenLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    if (filters.jahr === null) {
      setKundenLoading(true);
      listKundenAllFromOrdner()
        .then((list) => {
          if (!cancelled) setKunden(list);
        })
        .catch(() => {
          if (!cancelled) setKunden([]);
        })
        .finally(() => {
          if (!cancelled) setKundenLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    setKunden([]);
    return undefined;
  }, [filters.jahr]);

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr === "S") {
      setOrdner([]);
      return;
    }

    setOrdnerLoading(true);
    (async () => {
      try {
        if (typeof filters.jahr === "number") {
          const list = filters.kunde
            ? await listOrdnerForYearCustomer(filters.kunde, filters.jahr)
            : await listOrdner(filters.jahr);
          if (!cancelled) setOrdner(list);
          return;
        }
        if (filters.jahr === null) {
          const all = await listOrdner();
          const list = filters.kunde
            ? all.filter((o) => o.kunden_name === filters.kunde)
            : all;
          if (!cancelled) setOrdner(list);
          return;
        }
        if (!cancelled) setOrdner([]);
      } catch {
        if (!cancelled) setOrdner([]);
      } finally {
        if (!cancelled) setOrdnerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.jahr, filters.kunde]);

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr !== "S") return;
    setSOrdnerLoading(true);
    listSOrdner()
      .then((list) => {
        if (!cancelled) setSOrdner(list);
      })
      .catch(() => {
        if (!cancelled) setSOrdner([]);
      })
      .finally(() => {
        if (!cancelled) setSOrdnerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.jahr]);

  const ordnerDisplay = useMemo(
    () => dedupeOrdnersForChipDisplay(ordner, filters.jahr === null),
    [ordner, filters.jahr],
  );
  const sortedOrdnerForChips = useMemo(() => sortOrdnersForChipLayout(ordnerDisplay), [ordnerDisplay]);

  const sOrdnerDedup = useMemo(() => dedupeSOrdnerIds(sordner), [sordner]);
  const sortedSOrdnerForChips = useMemo(() => {
    const q = sordnerQuickFilter.trim().toLowerCase();
    const list = !q
      ? sOrdnerDedup
      : sOrdnerDedup.filter(
          (s) =>
            s.nummer.toLowerCase().includes(q) ||
            (s.beschreibung || "").toLowerCase().includes(q),
        );
    return sortSOrdnersForChipLayout(list);
  }, [sOrdnerDedup, sordnerQuickFilter]);

  const showProjektordner = filters.jahr !== "S";
  const showKundeOrdner = showProjektordner && (typeof filters.jahr === "number" || filters.jahr === null);

  const scrollRow =
    "-mx-0.5 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden px-0.5 pb-1.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]";
  const wrapRow = "flex flex-wrap gap-2";

  const tileSurface = clsx(
    "rounded-ios-lg bg-fill-quaternary/20 px-4 py-3 shadow-ios md:px-5 md:py-4",
    "ring-1 ring-separator/30 dark:ring-separator/45",
  );

  return (
    <>
      {isMobile ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            className="flex min-h-11 flex-1 items-center justify-between gap-3 rounded-ios-lg bg-fill-quaternary/20 px-4 py-2.5 text-left ring-1 ring-separator/30 dark:ring-separator/45"
            aria-expanded={filtersExpanded}
          >
            <span className="text-sm font-semibold text-label">
              {filtersExpanded ? "Filter ausblenden" : "Filter anzeigen"}
            </span>
            <span className="text-xs text-label-secondary">
              {hasActiveFilters ? "Aktiv" : "Alle"}
            </span>
          </button>
          {hasActiveFilters && onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="shrink-0 rounded-full bg-fill-tertiary px-3 py-2.5 text-sm font-semibold text-accent"
            >
              Zurücksetzen
            </button>
          ) : null}
        </div>
      ) : null}

      {showFilterBody ? (
      <div className="grid grid-cols-1 gap-3 md:gap-4 lg:gap-5 lg:[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)] lg:items-stretch [&>*]:min-w-0">
      {/* ─── Linke Kachel: Jahr, Kunde, Status ─── */}
      <div className={clsx(tileSurface, "flex min-h-[min-content] flex-col lg:min-h-0 lg:h-full")}>
        <Stack role="toolbar" aria-label="Jahr filtern">
          <SectionLabel right={yearsLoading ? <Spinner className="h-3.5 w-3.5 text-label-tertiary" /> : null}>
            Jahr
          </SectionLabel>
          <div className={scrollRow}>
            <ChipButton
              compact
              active={filters.jahr === null}
              disabled={yearsLoading}
              onClick={() => onChange({ jahr: null })}
            >
              Alle Jahre
            </ChipButton>
            {years.map((y) => (
              <ChipButton
                compact
                key={y}
                active={filters.jahr === y}
                disabled={yearsLoading}
                onClick={() => onChange({ jahr: y })}
              >
                {y}
              </ChipButton>
            ))}
          </div>
              <div className={clsx(scrollRow, "mt-2")}>
            <ChipButton compact active={filters.jahr === "S"} onClick={() => onChange({ jahr: "S" })}>
              S‑Ordner
            </ChipButton>
          </div>
        </Stack>

        {showKundeOrdner ? (
          <Stack className="mt-3" role="toolbar" aria-label="Kunde filtern">
            <SectionLabel right={kundenLoading ? <Spinner className="h-3.5 w-3.5 text-label-tertiary" /> : null}>
              Kunde
            </SectionLabel>
            <div className={wrapRow}>
              <ChipButton
                compact
                active={filters.kunde === ""}
                disabled={kundenLoading}
                onClick={() => onChange({ kunde: "" })}
              >
                Alle
              </ChipButton>
              {kunden.map((k) => (
                <ChipButton
                  compact
                  key={k}
                  active={filters.kunde === k}
                  disabled={kundenLoading}
                  onClick={() => onChange({ kunde: filters.kunde === k ? "" : k })}
                  title={k}
                >
                  {k}
                </ChipButton>
              ))}
            </div>
          </Stack>
        ) : null}

        <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-2">
          <div className="min-w-[min(100%,12rem)] flex-1 space-y-1">
            <SectionLabel>Status</SectionLabel>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Status filtern (Mehrfachauswahl)">
              {statusFilterOptions.map((opt) => {
                const active =
                  opt.value === "alle"
                    ? filters.status.length === 0
                    : filters.status.includes(opt.value);
                return (
                  <ChipButton
                    key={opt.value}
                    compact
                    active={active}
                    onClick={() =>
                      onChange({ status: toggleStatusFilter(filters.status, opt.value) })
                    }
                  >
                    {opt.label}
                  </ChipButton>
                );
              })}
            </div>
          </div>
          {projectHasActiveFilters(filters) && onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="mb-px shrink-0 text-sm font-semibold text-accent hover:underline"
            >
              Zurücksetzen
            </button>
          ) : null}
        </div>
      </div>

      {/* ─── Rechte Kachel: Projektordner oder S‑Ordner ─── */}
      {showKundeOrdner ? (
        <div className={clsx(tileSurface, "flex min-h-[min-content] flex-col lg:min-h-0 lg:h-full")}>
          <Stack>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="min-w-[4rem] space-y-1">
                <SectionLabel right={ordnerLoading ? <Spinner className="h-3.5 w-3.5 text-label-tertiary" /> : null}>
                  Ordner
                </SectionLabel>
                <span className="inline-block rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-medium text-label-secondary tabular-nums md:text-[13px]">
                  {ordnerLoading ? "…" : `${ordnerDisplay.length} Ordner`}
                </span>
              </div>
              <div className="ml-auto flex shrink-0">
                <button
                  type="button"
                  onClick={() => setOrdnerModalOpen(true)}
                  className="rounded-full bg-fill-tertiary px-4 py-2 text-sm font-medium text-label hover:bg-fill"
                >
                  Liste&nbsp;&amp;&nbsp;Suche
                </button>
              </div>
            </div>
          </Stack>

          <div className="mt-3 max-h-[min(32vh,16rem)] min-w-0 flex-1 overflow-y-auto pr-1 md:max-h-[min(48vh,22rem)] [scrollbar-width:thin]">
            {ordnerLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
                <Spinner className="h-4 w-4" />
                Ordner werden geladen …
              </div>
            ) : ordnerDisplay.length === 0 ? (
              <p className="py-4 text-sm text-label-tertiary">Keine Ordner für diese Auswahl.</p>
            ) : (
              <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2.5">
                <ChipButton compact active={!filters.ordner} onClick={() => onChange({ ordner: "" })}>
                  Alle
                </ChipButton>
                <div aria-hidden className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden" />
                {sortedOrdnerForChips.flatMap((o, idx) => {
                  const prev = sortedOrdnerForChips[idx - 1];
                  const needBreak =
                    idx > 0 &&
                    typeof prev !== "undefined" &&
                    ordnerKeyPrefix(prev.nummer) !== ordnerKeyPrefix(o.nummer);
                  const chip = (
                    <ChipButton
                      compact
                      key={o.id}
                      active={filters.ordner === o.nummer}
                      onClick={() =>
                        onChange({ ordner: filters.ordner === o.nummer ? "" : o.nummer })
                      }
                      title={[o.kunden_name, o.beschreibung].filter(Boolean).join(" · ")}
                    >
                      {o.nummer}
                    </ChipButton>
                  );
                  return needBreak
                    ? [
                        <div
                          key={`pref-br-${idx}`}
                          aria-hidden
                          className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                        />,
                        chip,
                      ]
                    : [chip];
                })}
              </div>
            )}
          </div>
        </div>
      ) : filters.jahr === "S" ? (
        <div className={clsx(tileSurface, "flex min-h-[min-content] flex-col lg:min-h-0 lg:h-full")}>
          <Stack>
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <div className="space-y-1">
                <SectionLabel right={sordnerLoading ? <Spinner className="h-3.5 w-3.5 text-label-tertiary" /> : null}>
                  S‑Ordner
                </SectionLabel>
              </div>
              <input
                type="search"
                value={sordnerQuickFilter}
                onChange={(e) => setSordnerQuickFilter(e.target.value)}
                placeholder="S‑Ordner suchen …"
              className="eh-input min-w-[12rem] max-w-full flex-1 py-2 text-sm"
                aria-label="S-Ordner eingrenzen"
              />
            </div>
          </Stack>
          <div className="mt-3 max-h-[min(32vh,16rem)] min-w-0 flex-1 overflow-y-auto pr-1 md:max-h-[min(48vh,22rem)] [scrollbar-width:thin]">
            {sordnerLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
                <Spinner className="h-4 w-4" />
                S‑Ordner werden geladen …
              </div>
            ) : sOrdnerDedup.length === 0 ? (
              <p className="py-4 text-sm text-label-tertiary">Keine S‑Ordner.</p>
            ) : (
              <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2.5">
                <ChipButton compact active={!filters.sordner} onClick={() => onChange({ sordner: "" })}>
                  Alle
                </ChipButton>
                <div
                  aria-hidden
                  className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                />
                {sortedSOrdnerForChips.flatMap((s, idx) => {
                  const prev = sortedSOrdnerForChips[idx - 1];
                  const needBreak =
                    idx > 0 &&
                    typeof prev !== "undefined" &&
                    sOrdnerDecadeKey(prev.nummer) !== sOrdnerDecadeKey(s.nummer);
                  const chipTitleParts = [
                    buildSOrdnerHeadingLine(s),
                    formatSOrdnerLocation(s),
                  ].filter(Boolean);
                  const chip = (
                    <ChipButton
                      compact
                      key={s.id}
                      active={filters.sordner === s.nummer}
                      onClick={() =>
                        onChange({ sordner: filters.sordner === s.nummer ? "" : s.nummer })
                      }
                      onDoubleClick={() => {
                        if (filters.sordner === s.nummer) onSOrdnerOpen?.(s);
                      }}
                      title={
                        chipTitleParts.length
                          ? `${chipTitleParts.join("\n")}${onSOrdnerOpen ? "\n\nDoppelklick: S-Ordner öffnen" : ""}`
                          : onSOrdnerOpen
                            ? "Doppelklick: S-Ordner öffnen"
                            : undefined
                      }
                    >
                      {s.nummer}
                    </ChipButton>
                  );
                  return needBreak
                    ? [
                        <div
                          key={`s-pref-br-${idx}`}
                          aria-hidden
                          className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                        />,
                        chip,
                      ]
                    : [chip];
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}
      </div>
      ) : null}

      <OrdnerPickerModal
        open={ordnerModalOpen}
        onClose={() => setOrdnerModalOpen(false)}
        ordner={ordnerDisplay}
        loading={ordnerLoading}
        value={filters.ordner}
        onSelect={(o) => {
          onChange({ ordner: o });
          setOrdnerModalOpen(false);
        }}
      />
    </>
  );
}

function Stack({ className, ...props }: ComponentProps<"div">) {
  return <div className={clsx("space-y-1.5", className)} {...props} />;
}

function SectionLabel({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-[1.25rem] items-center justify-between gap-2 md:min-h-[1.375rem]">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary md:text-xs">
        {children}
      </span>
      {right}
    </div>
  );
}


function ordnerKeyPrefix(nummer: string): string {
  const t = nummer.trim();
  const i = t.indexOf("-");
  if (i <= 0) return t.length ? t : "\u0000";
  const head = t.slice(0, i).trim();
  return head.length ? head : "\u0000";
}

function comparePrefixKeys(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  const aIsNum = Number.isFinite(na) && String(na) === a;
  const bIsNum = Number.isFinite(nb) && String(nb) === b;
  if (aIsNum && bIsNum) return na - nb;
  return a.localeCompare(b, "de", { numeric: true });
}

/** Bei „Alle Jahre“ filtert die UI nur nach Ordnernummer — eine Chip pro Nummer. */
function dedupeOrdnersForChipDisplay(list: OrdnerOut[], allYears: boolean): OrdnerOut[] {
  const byId = dedupeOrdnerIds(list);
  if (allYears) {
    const byNummer = new Map<string, OrdnerOut>();
    for (const o of byId) {
      const key = o.nummer.trim().toLowerCase();
      const existing = byNummer.get(key);
      if (!existing || (o.jahr ?? 0) > (existing.jahr ?? 0)) {
        byNummer.set(key, o);
      }
    }
    return [...byNummer.values()];
  }
  return dedupeOrdnersByJahrNummer(byId);
}

function dedupeOrdnersByJahrNummer(list: OrdnerOut[]): OrdnerOut[] {
  const seen = new Set<string>();
  const out: OrdnerOut[] = [];
  for (const o of list) {
    const key = `${o.jahr}\u0000${o.nummer.trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(o);
  }
  return out;
}

function dedupeOrdnerIds(list: OrdnerOut[]): OrdnerOut[] {
  const seen = new Set<number>();
  const out: OrdnerOut[] = [];
  for (const o of list) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push(o);
  }
  return out;
}

function dedupeSOrdnerIds(list: SOrdnerOut[]): SOrdnerOut[] {
  const seen = new Set<number>();
  const out: SOrdnerOut[] = [];
  for (const s of list) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    out.push(s);
  }
  return out;
}

function sortOrdnersForChipLayout(list: OrdnerOut[]): OrdnerOut[] {
  return [...list].sort((a, b) => {
    const cmp = comparePrefixKeys(ordnerKeyPrefix(a.nummer), ordnerKeyPrefix(b.nummer));
    if (cmp !== 0) return cmp;
    return a.nummer.localeCompare(b.nummer, "de", { numeric: true });
  });
}

/** Numerischer Anteil nach „S“ (S01 → 1, S100 → 100). */
function parseSOrdnerNumber(nummer: string): number | null {
  const m = String(nummer).trim().match(/^S(\d+)/i);
  const digits = m?.[1];
  if (!digits) return null;
  const n = Number.parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
}

/** Zehner-Gruppe: 0 → S01–S09, 1 → S10–S19, 10 → S100–S109, … */
function sOrdnerDecadeKey(nummer: string): number {
  const n = parseSOrdnerNumber(nummer);
  if (n == null) return Number.MAX_SAFE_INTEGER;
  return Math.floor(n / 10);
}

function sortSOrdnersForChipLayout(list: SOrdnerOut[]): SOrdnerOut[] {
  return [...list].sort((a, b) => {
    const na = parseSOrdnerNumber(a.nummer);
    const nb = parseSOrdnerNumber(b.nummer);
    if (na != null && nb != null && na !== nb) return na - nb;
    if (na != null && nb == null) return -1;
    if (na == null && nb != null) return 1;
    return a.nummer.localeCompare(b.nummer, "de", { numeric: true });
  });
}

/* ─────────────────────────── Bausteine ─────────────────────────── */

interface ChipButtonProps {
  active?: boolean;
  compact?: boolean;
  disabled?: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  children: React.ReactNode;
  title?: string;
}

function ChipButton({
  active,
  compact,
  disabled,
  onClick,
  onDoubleClick,
  children,
  title,
}: ChipButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={title}
      disabled={disabled}
      className={clsx(
        "shrink-0 rounded-full font-medium transition",
        compact ? "min-h-10 px-3 py-2 text-sm md:min-h-0 md:px-3 md:py-1.5" : "px-4 py-2 text-base",
        disabled && "cursor-not-allowed opacity-45",
        active
          ? "bg-accent text-label-on-accent shadow-sm"
          : clsx(!disabled && "hover:bg-fill", "bg-fill-tertiary text-label"),
      )}
    >
      {children}
    </button>
  );
}

